import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { Menu as MenuIcon, Close } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import LanguageSwitcher from "components/LanguageSwitcher";
import SiteSearchDialog from "components/SiteSearchDialog";
import logoWhite from "assets/icon-white-transparent.png";
import { SG, TEXT, PURPLE, PURPLE_HOVER } from "theme/publicDarkTokens";

/**
 * Dark variant of the public site header, matching the Winner Express
 * landing redesign (deep-violet glass nav). Shared so the same pattern
 * can be rolled out to the other public pages later.
 */
const PublicHeaderDark = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:900px)");

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Open site search with Ctrl/Cmd + K from anywhere on the public site.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const menuItems = [
    { label: t("navigation.home"), path: "/" },
    { label: t("navigation.trackPackage"), path: "/track" },
    { label: t("navigation.calculator"), path: "/calculator" },
    { label: t("navigation.services"), path: "/services" },
    { label: t("navigation.about"), path: "/about" },
    { label: t("navigation.contact"), path: "/contact" },
  ];

  const handleMobileNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <Box
      component="nav"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        height: 72,
        background: "rgba(7,4,26,.9)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderBottom: "1px solid rgba(248,245,255,.06)",
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: "auto",
          px: { xs: 2.5, md: 5 },
          height: "100%",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {/* Brand */}
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            cursor: "pointer",
            flexShrink: 0,
            mr: "auto",
          }}
        >
          <Box
            component="img"
            src={logoWhite}
            alt="Winner Express"
            sx={{ width: 32, height: 32, objectFit: "contain" }}
          />
          <Typography
            sx={{
              fontFamily: SG,
              fontWeight: 700,
              fontSize: 17,
              color: TEXT,
              letterSpacing: "-.022em",
            }}
          >
            {t("home.title")}
          </Typography>
        </Box>

        {/* Desktop nav links */}
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 3.25, flexShrink: 0 }}>
            {menuItems.map((item) => (
              <Box
                key={item.path}
                component="a"
                onClick={() => navigate(item.path)}
                sx={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontSize: 14,
                  fontWeight: isActive(item.path) ? 700 : 500,
                  letterSpacing: ".01em",
                  color: isActive(item.path) ? TEXT : "rgba(248,245,255,.58)",
                  textDecoration: "none",
                  transition: "color .2s",
                  "&:hover": { color: TEXT },
                }}
              >
                {item.label}
              </Box>
            ))}
          </Box>
        )}

        {/* Right actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
          {!isMobile && (
            <>
              <Box sx={{ color: "rgba(248,245,255,.6)" }}>
                <LanguageSwitcher />
              </Box>
              <Button
                disableElevation
                variant="contained"
                color="secondary"
                onClick={() => navigate("/login")}
                sx={{
                  bgcolor: PURPLE,
                  color: "#fff",
                  fontFamily: SG,
                  fontWeight: 600,
                  fontSize: 13,
                  textTransform: "none",
                  letterSpacing: ".02em",
                  px: 2.75,
                  py: 1.1,
                  borderRadius: "10px",
                  "&:hover": {
                    bgcolor: PURPLE_HOVER,
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 28px rgba(124,58,237,.5)",
                  },
                }}
              >
                {t("navigation.login")}
              </Button>
            </>
          )}
          {isMobile && (
            <>
              <Box sx={{ color: "rgba(248,245,255,.6)" }}>
                <LanguageSwitcher />
              </Box>
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{ color: TEXT }}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            boxSizing: "border-box",
            background: "#0C0820",
            color: TEXT,
            borderLeft: "1px solid rgba(248,245,255,.08)",
          },
        }}
      >
        <Box sx={{ p: 1, display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: TEXT }}>
            <Close />
          </IconButton>
        </Box>
        <List>
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={() => handleMobileNavigate(item.path)}
                  sx={{
                    py: 1.75,
                    borderLeft: "4px solid",
                    borderLeftColor: active ? PURPLE : "transparent",
                    backgroundColor: active ? "rgba(124,58,237,.16)" : "transparent",
                    "&:hover": { backgroundColor: "rgba(124,58,237,.12)" },
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontWeight: active ? 700 : 500,
                        color: active ? "#C084FC" : "rgba(248,245,255,.8)",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider sx={{ mt: 2, borderColor: "rgba(248,245,255,.08)" }} />
        <Box sx={{ p: 2 }}>
          <Button
            disableElevation
            fullWidth
            variant="contained"
            color="secondary"
            onClick={() => handleMobileNavigate("/login")}
            sx={{
              bgcolor: PURPLE,
              color: "#fff",
              fontFamily: SG,
              fontWeight: 600,
              textTransform: "none",
              py: 1.5,
              borderRadius: "10px",
              "&:hover": { bgcolor: PURPLE_HOVER },
            }}
          >
            {t("navigation.login")}
          </Button>
        </Box>
      </Drawer>

      <SiteSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </Box>
  );
};

export default PublicHeaderDark;
